import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const authData = await authService.checkAuthStatus();
      
      if (authData.authenticated) {
        setUser(authData.user);
        setSession(authData.session);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setSession(null);
        setIsAuthenticated(false);
        
        if (authData.error) {
          setError(authData.error);
        }
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      setError(error.message);
      setIsAuthenticated(false);
      setUser(null);
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const result = await authService.login(username, password);
      
      // Update state immediately after successful login
      setUser(result.user);
      setIsAuthenticated(true);
      setSession(null); // Django auth doesn't use sessions
      
      return result;
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await authService.logout();
      
      // Update state immediately after logout
      setUser(null);
      setSession(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      setError(error.message);
      
      // Clear local state even if logout API fails
      setUser(null);
      setSession(null);
      setIsAuthenticated(false);
    }
  };

  // Development helper function - now just creates a test user via login
  const devLogin = async () => {
    try {
      setError(null);
      // Try to login with test credentials
      await login('testuser', 'testpass123');
    } catch (error) {
      console.error('Dev login failed:', error);
      setError('Dev login failed. Make sure test user exists.');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    // State
    user,
    session,
    isAuthenticated,
    isLoading,
    error,
    
    // Actions
    login,
    logout,
    devLogin,
    checkAuthStatus,
    clearError,
    
    // Helper functions
    getUserDisplayName: () => {
      if (!user) return '';
      return user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.email;
    },
    
    getSessionId: () => {
      return session?.session_id || '';
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
